import { useState, useEffect } from 'react';

/**
 * Custom hook to manage file thumbnails (images and videos)
 *
 * For IMAGES: downloads the file and creates a blob URL (files are typically small).
 * For VIDEOS: fetches /api/files/{id}/thumbnail which returns a pre-generated JPEG
 *             extracted by ffmpeg at upload time — a ~20-50 KB download instead of
 *             the full video. Falls back to 'failed' (shows icon) if no thumbnail
 *             exists for older videos.
 */
export const useFileThumbnails = (files) => {
  const [thumbnailUrls, setThumbnailUrls] = useState({});

  useEffect(() => {
    const loadThumbnails = async () => {
      const mediaFiles = files.filter(f => f.mime_type?.startsWith('image/') || f.mime_type?.startsWith('video/'));
      const token = localStorage.getItem('token');
      const baseUrl = window.location.origin;

      for (const file of mediaFiles) {
        if (thumbnailUrls[file.id]) continue; // Already loaded

        try {
          if (file.mime_type?.startsWith('video/')) {
            // ── VIDEO: fetch the tiny pre-generated thumbnail (~20-50 KB) ────────
            // The full video (40-100 MB) is NOT downloaded.
            const response = await fetch(`${baseUrl}/api/files/${file.id}/thumbnail`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              const blob = await response.blob();
              const url = URL.createObjectURL(blob);
              setThumbnailUrls(prev => ({ ...prev, [file.id]: url }));
            } else {
              // No thumbnail stored yet (older videos before ffmpeg was added)
              setThumbnailUrls(prev => ({ ...prev, [file.id]: 'failed' }));
            }
          } else {
            // ── IMAGE: download the file directly ────────────────────────────────
            const response = await fetch(`${baseUrl}/api/files/${file.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              const blob = await response.blob();
              const url = URL.createObjectURL(blob);
              setThumbnailUrls(prev => ({ ...prev, [file.id]: url }));
            }
          }
        } catch (err) {
          console.warn('Failed to load thumbnail for', file.id);
          setThumbnailUrls(prev => ({ ...prev, [file.id]: 'failed' }));
        }
      }
    };

    if (files.length > 0) {
      loadThumbnails();
    }

    // Cleanup blob URLs on unmount
    return () => {
      Object.values(thumbnailUrls).forEach(url => {
        if (url && url.startsWith('blob:')) {
          try { URL.revokeObjectURL(url); } catch(e) { /* ignore */ }
        }
      });
    };
  }, [files]); // eslint-disable-line

  return thumbnailUrls;
};
